import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import _ from 'lodash';

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

dayjs.locale('th');
dayjs.extend(buddhistEra);
dayjs.extend(LocalizedFormat);

const LEAVE_LABEL = {
  SICK: 'ลาป่วย',
  PERSONAL: 'ลากิจ',
  VACATION: 'ลาพักร้อน',
  MATERNITY: 'ลาคลอด',
  ORDINATION: 'ลาบวช',
  UNPAID: 'ลาไม่รับค่าจ้าง',
};

const PERIOD_LABEL = {
  FULL_DAY: 'เต็มวัน',
  MORNING: 'ครึ่งวันเช้า',
  AFTERNOON: 'ครึ่งวันบ่าย',
};

const LEAVE_BG = '#fce7f3';
const LEAVE_OK_BG = '#dcfce7';
const LEAVE_WARN_BG = '#fecaca';

// สร้าง map: dateKey (YYYY-MM-DD) → leave record (เฉพาะวันในช่วงลา)
const buildLeaveByDate = (empLeaves) => {
  const map = {};
  empLeaves.forEach((lr) => {
    let d = dayjs(lr.start_date).startOf('day');
    const lEnd = dayjs(lr.end_date).startOf('day');
    while (!d.isAfter(lEnd)) {
      map[d.format('YYYY-MM-DD')] = lr;
      d = d.add(1, 'day');
    }
  });
  return map;
};

// สร้าง rows สำหรับตารางหลัก — รวม checkin + leave เข้าด้วยกัน เรียงตามวันที่
const genBodyWithLeave = (timesTampData, empLeaves) => {
  const leaveByDate = buildLeaveByDate(empLeaves);
  const allDays = {};

  // ใส่ checkin ก่อน
  (timesTampData || []).forEach((row) => {
    const key = dayjs(row?.checkIn).format('YYYY-MM-DD');
    allDays[key] = { checkin: row, leave: leaveByDate[key] || null };
  });

  // ใส่วันลาที่ไม่มี checkin
  Object.entries(leaveByDate).forEach(([key, lr]) => {
    if (!allDays[key]) allDays[key] = { checkin: null, leave: lr };
  });

  const sorted = Object.entries(allDays)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, data]) => data);

  return sorted.map(({ checkin, leave }, idx) => {
    const isHalf = leave && leave.period !== 'FULL_DAY';
    const leaveLabel = leave ? (LEAVE_LABEL[leave.leave_type] || leave.leave_type) : '';
    const periodLabel = leave ? (PERIOD_LABEL[leave.period] || leave.period) : '';
    const nonLeavePeriod = leave?.period === 'MORNING' ? 'ช่วงบ่าย' : 'ช่วงเช้า';

    if (!checkin && leave) {
      // วันลาเต็มวัน หรือลาครึ่งวันแต่ไม่มี checkin
      const noCheckinNote = isHalf ? `ไม่มีการลงเวลา${nonLeavePeriod}` : '-';
      return [
        { text: idx + 1, alignment: 'center', fillColor: LEAVE_BG },
        { text: leaveLabel, alignment: 'center', fillColor: LEAVE_BG, bold: true },
        { text: dayjs(leave.start_date).format('DD/MM/YYYY'), alignment: 'center', fillColor: LEAVE_BG },
        { text: `— ${periodLabel} —`, alignment: 'center', fillColor: LEAVE_BG, italics: true, colSpan: 2 },
        {},
        { text: '', fillColor: LEAVE_BG },
        { text: '', fillColor: LEAVE_BG },
        { text: '', fillColor: LEAVE_BG },
        { text: isHalf ? noCheckinNote : '', alignment: 'center', fillColor: isHalf ? LEAVE_WARN_BG : LEAVE_BG, fontSize: 9 },
      ];
    }

    if (checkin && leave && isHalf) {
      // ลาครึ่งวัน + มี checkin → ตรวจสอบ timing
      const checkinHour = dayjs(checkin.checkIn).hour();
      const inLeavePeriod = leave.period === 'MORNING' ? checkinHour < 12 : checkinHour >= 12;
      const statusText = inLeavePeriod
        ? `⚠ ลงเวลาในช่วงลา`
        : `✓ ลงเวลา${nonLeavePeriod}`;
      const statusBg = inLeavePeriod ? LEAVE_WARN_BG : LEAVE_OK_BG;

      return [
        { text: idx + 1, alignment: 'center' },
        { text: checkin?.projectDetails?.name || '', alignment: 'center' },
        { text: dayjs(checkin.checkIn).format('DD/MM/YYYY'), alignment: 'center' },
        { text: dayjs(checkin.checkIn).format('HH:mm'), alignment: 'center' },
        { text: checkin?.checkOut ? dayjs(checkin.checkOut).format('HH:mm') : '', alignment: 'center' },
        { text: `${checkin?.normal_time || ''}`, alignment: 'center' },
        { text: `${checkin?.ot_show || ''}`, alignment: 'center' },
        { text: `${leaveLabel}\n${periodLabel}`, fillColor: LEAVE_BG, alignment: 'center', fontSize: 9 },
        { text: statusText, fillColor: statusBg, alignment: 'center', fontSize: 9 },
      ];
    }

    // checkin ปกติ ไม่มีการลา
    return [
      { text: idx + 1, alignment: 'center' },
      { text: checkin?.projectDetails?.name || '', alignment: 'center' },
      { text: dayjs(checkin.checkIn).format('DD/MM/YYYY'), alignment: 'center' },
      { text: dayjs(checkin.checkIn).format('HH:mm'), alignment: 'center' },
      { text: checkin?.checkOut ? dayjs(checkin.checkOut).format('HH:mm') : '', alignment: 'center' },
      { text: `${checkin?.normal_time || ''}`, alignment: 'center' },
      { text: `${checkin?.ot_show || ''}`, alignment: 'center' },
      { text: '', alignment: 'center' },
      { text: '', alignment: 'center' },
    ];
  });
};

// ตาราง Leave Summary ท้าย — แสดงทุก leave ของพนักงานคนนี้
const genLeaveSummary = (empLeaves) => {
  if (!empLeaves.length) return null;

  const rows = [];
  empLeaves.forEach((lr) => {
    let d = dayjs(lr.start_date).startOf('day');
    const lEnd = dayjs(lr.end_date).startOf('day');
    while (!d.isAfter(lEnd)) {
      rows.push({
        date: d.format('DD/MM/YYYY'),
        type: LEAVE_LABEL[lr.leave_type] || lr.leave_type,
        period: PERIOD_LABEL[lr.period] || lr.period,
        reason: lr.reason || '-',
        isHalf: lr.period !== 'FULL_DAY',
        nonLeavePeriod: lr.period === 'MORNING' ? 'ช่วงบ่าย' : 'ช่วงเช้า',
      });
      d = d.add(1, 'day');
    }
  });

  return {
    margin: [0, 10, 0, 0],
    table: {
      widths: ['10%', '18%', '14%', '*', '20%'],
      body: [
        [
          { text: 'วันที่ลา', fillColor: '#e5e7eb', alignment: 'center', bold: true, fontSize: 10 },
          { text: 'ประเภทการลา', fillColor: '#e5e7eb', alignment: 'center', bold: true, fontSize: 10 },
          { text: 'ช่วงเวลา', fillColor: '#e5e7eb', alignment: 'center', bold: true, fontSize: 10 },
          { text: 'เหตุผล', fillColor: '#e5e7eb', alignment: 'center', bold: true, fontSize: 10 },
          { text: 'หมายเหตุ HR', fillColor: '#e5e7eb', alignment: 'center', bold: true, fontSize: 10 },
        ],
        ...rows.map((r) => [
          { text: r.date, alignment: 'center', fontSize: 10 },
          { text: r.type, alignment: 'center', fontSize: 10, fillColor: LEAVE_BG },
          { text: r.period, alignment: 'center', fontSize: 10 },
          { text: r.reason, fontSize: 10 },
          { text: r.isHalf ? `ตรวจสอบการลงเวลา${r.nonLeavePeriod}` : '', fontSize: 9, color: '#6b7280' },
        ]),
      ],
    },
  };
};

const timestampReportPersonal = async (row, leaveData = []) => {
  const documentRef = {
    pageSize: 'A4',
    pageOrientation: 'LandScape',
    pageMargins: [20, 80, 20, 60],
    defaultStyle: {
      font: 'Sarabun',
      fontSize: '12',
    },
    info: { title: 'Employee Time Sheet Report' },
    header: [
      {
        margin: [0, 12],
        table: {
          alignment: '',
          headerRows: 1,
          widths: ['100%'],
          body: [
            [
              {
                text: 'Employee Time Sheet Report',
                style: 'headerPage',
                border: [false, false, false, false],
              },
            ],
          ],
        },
      },
    ],
    content: _.map(row, (e) => {
      // หา leave records ของพนักงานคนนี้
      const empId = String(e?.employeeDetails?._id || e?.employee_id || '');
      const empLeaves = leaveData.filter(
        (lr) => String(lr.employee?._id || lr.employee) === empId,
      );

      const tableBody = genBodyWithLeave(e?.timesTampData, empLeaves);
      const leaveSummary = genLeaveSummary(empLeaves);

      return [
        // ── header พนักงาน ──
        {
          margin: [6, 4],
          table: {
            headerRows: 1,
            widths: ['auto', '20%', 'auto', '*'],
            body: [
              [
                { text: 'ชื่อ - สกุล :', margin: [8, 0], alignment: 'right', border: [false, false, false, false] },
                {
                  text: `${e?.employeeDetails?.firstname || ''} ${e?.employeeDetails?.lastname || ''}`,
                  border: [false, false, false, false],
                  bold: true,
                },
                { text: 'ตำแหน่ง :', alignment: 'right', border: [false, false, false, false] },
                { text: e?.employeeDetails?.department?.name || '', border: [false, false, false, false] },
              ],
            ],
          },
        },

        // ── ตารางการลงเวลา + การลา ──
        {
          table: {
            headerRows: 1,
            widths: ['5%', '20%', '10%', '7%', '7%', '7%', '7%', '18%', '19%'],
            body: [
              [
                { text: 'ลำดับ', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'โครงการ', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'วันที่', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'เข้า', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'ออก', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'ปกติ', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'OT', fillColor: '#d2d4d2', alignment: 'center', bold: true },
                { text: 'ประเภทการลา', fillColor: '#f9a8d4', alignment: 'center', bold: true },
                { text: 'สถานะการลงเวลา', fillColor: '#f9a8d4', alignment: 'center', bold: true },
              ],
              ...(tableBody.length ? tableBody : [[
                { text: '-', colSpan: 9, alignment: 'center', color: '#9ca3af' },
                {}, {}, {}, {}, {}, {}, {}, {},
              ]]),
            ],
          },
        },

        // ── สรุปการลา ──
        ...(leaveSummary
          ? [
              { text: 'สรุปการลา', bold: true, margin: [0, 12, 0, 4], fontSize: 11 },
              leaveSummary,
            ]
          : []),

        // page break ระหว่างพนักงาน
        { text: '', pageBreak: 'after' },
      ];
    }),

    footer: [],
    styles: {
      headerPage: { fontSize: 14, bold: true, alignment: 'center' },
      header: { fontSize: 14, bold: true, alignment: 'center' },
    },
  };

  pdfMake.createPdf(documentRef).open();
};

export default timestampReportPersonal;
